import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowDownTrayIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import { toast } from "../../../utils/toast";
import {
  reviewDealDocument,
  uploadRequiredDocuments,
  uploadSingleFile,
} from "../../../services/api";
import {
  DealResponseDto,
} from "../../../types/types";
import type { TranslationFn } from "../createDeal/dealFormFields";
import { getOutcomeRegistryRows } from "../../../utils/opportunities/outcomeRegistryHelpers";
import { getTemplateStepId, getOptionValue, getOptionLabel, addOptionLabel } from "../../../utils/opportunities/templateDisplayLabels";
import { asRecord } from "../../../utils/typeGuards";

const toTitleCase = (str: string): string =>
  String(str)
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();
  
const flattenTranslationValues = (
  obj: Record<string, unknown>,
  result: Record<string, string> = {},
): Record<string, string> => {
  Object.entries(obj).forEach(([key, value]) => {
    if (typeof value === "string") {
      result[value] = key;
    } else if (value && typeof value === "object") {
      flattenTranslationValues(value as Record<string, unknown>, result);
    }
  });

  return result;
};

type TemplateDisplayMeta = {
  stepLabels: Record<string, string>;
  fieldLabels: Record<string, string>;
  fieldTypes: Record<string, string>;
  optionLabels: Record<string, Record<string, string>>;
};

type TemplateStepShape = {
  stepId?: string;
  id?: string;
  key?: string;
  title?: string;
  name?: string;
  label?: string;
  fields?: unknown;
  outcomeRegistry?: unknown;
};

type TemplateFieldShape = {
  key?: string;
  label?: string;
  title?: string;
  name?: string;
  type?: string;
  options?: unknown;
  fields?: unknown;
  optionsFrom?: string;
};

type TemplateOptionShape = {
  value?: unknown;
  key?: unknown;
  id?: unknown;
  label?: unknown;
  title?: unknown;
  text?: unknown;
  name?: unknown;
};

type SourceShape = Record<string, unknown>;

const emptyTemplateDisplayMeta: TemplateDisplayMeta = {
  stepLabels: {},
  fieldLabels: {},
  fieldTypes: {},
  optionLabels: {},
};

const isObjectValue = (value: unknown): value is SourceShape =>
  !!value && typeof value === "object" && !Array.isArray(value);

const getFirstValue = (...values: unknown[]) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const isHttpUrl = (value: string): boolean => /^https?:\/\//i.test(value);

const getFileNameFromUrl = (value: string): string => {
  try {
    const url = new URL(value);
    return decodeURIComponent(
      url.pathname.split("/").filter(Boolean).pop() || "",
    );
  } catch {
    return decodeURIComponent(value.split("/").pop()?.split("?")[0] || "");
  }
};

const FILE_UPLOAD_TYPES = new Set(["file", "file_upload", "document_upload"]);

const FILE_EXTENSION_REGEX =
  /\.(pdf|doc|docx|xls|xlsx|csv|txt|rtf|zip|rar|jpg|jpeg|png|gif|webp|mp4|mov|avi|wmv|mp3|wav)$/i;

type FileDisplayItem = {
  name: string;
  url: string;
  extension: string;
};

const getFileExtension = (fileName: string): string => {
  const match = fileName.match(/\.([a-z0-9]+)$/i);
  return match?.[1]?.toUpperCase() || "";
};

const getFileDisplayName = (value: string): string =>
  getFileNameFromUrl(value) || value || "file";

const normalizeFileItems = (value: unknown): FileDisplayItem[] => {
  if (Array.isArray(value)) return value.flatMap(normalizeFileItems);

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    if (!trimmedValue) return [];

    const name = getFileDisplayName(trimmedValue);
    return [
      {
        name,
        url: isHttpUrl(trimmedValue) ? trimmedValue : "",
        extension: getFileExtension(name),
      },
    ];
  }

  if (isObjectValue(value)) {
    const rawUrl = getFirstValue(
      value.fileUrl,
      value.url,
      value.link,
      value.href,
      value.path,
    );
    const rawName = getFirstValue(
      value.fileName,
      value.name,
      value.originalFileName,
      rawUrl,
    );

    if (!rawUrl && !rawName) return [];

    const url =
      typeof rawUrl === "string" && isHttpUrl(rawUrl.trim())
        ? rawUrl.trim()
        : "";
    const nameSource =
      typeof rawName === "string" ? rawName.trim() : String(rawName || "");
    const name = getFileDisplayName(nameSource || url);

    return [
      {
        name,
        url,
        extension: getFileExtension(name),
      },
    ];
  }

  return [];
};

const hasFileExtension = (file: FileDisplayItem): boolean =>
  FILE_EXTENSION_REGEX.test(file.name) ||
  (!!file.url && FILE_EXTENSION_REGEX.test(getFileNameFromUrl(file.url)));

const downloadFileFromUrl = async (url: string, fileName: string) => {
  const response = await fetch(url);
  if (!response.ok) throw new Error("Download failed");

  const blob = await response.blob();
  const downloadUrl = window.URL.createObjectURL(blob);

  try {
    const anchorElement = document.createElement("a");
    anchorElement.href = downloadUrl;
    anchorElement.download = fileName || getFileNameFromUrl(url) || "download";
    anchorElement.style.display = "none";
    document.body.appendChild(anchorElement);
    anchorElement.click();
    anchorElement.remove();
  } finally {
    window.setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 1000);
  }
};

const getFirstFieldEntry = (
  ...entries: Array<{ key: string; value: unknown; source?: unknown }>
) =>
  entries.find(
    (entry) =>
      entry.value !== undefined && entry.value !== null && entry.value !== "",
  );

const getLookupValue = (source: unknown, key: string) => {
  if (!isObjectValue(source) || !key) return undefined;
  if (source[key] !== undefined) return source[key];

  const matchedKey = Object.keys(source).find(
    (sourceKey) => sourceKey.toLowerCase() === key.toLowerCase(),
  );

  return matchedKey ? source[matchedKey] : undefined;
};

const DATE_FIELD_KEYS = new Set([
  "createdAt",
  "updatedAt",
  "uploadedAt",
  "reviewedAt",
  "approvedAt",
  "rejectedAt",
  "confirmedAt",
  "deadlineAt",
  "deadlineDate",
  "eventDate",
  "startDate",
  "enDate",
  "dueOn",
  "paidOn",
]);

const formatDateValue = (value: string, fieldPath?: string): string | null => {
  const fieldKey = fieldPath?.split(".").pop() || "";
  if (!DATE_FIELD_KEYS.has(fieldKey)) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const hasTime = value.includes("T") || /At$/.test(fieldKey);
  return hasTime
    ? date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    : date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
};

const getValueLabelFromSource = (
  source: unknown,
  key: string,
  value: unknown,
): string | undefined => {
  if (!isObjectValue(source) || !key) return undefined;

  const src = source as Record<string, unknown>;
  const valueKey = value === undefined || value === null ? "" : String(value);
  const directLabel = getFirstValue(
    src[`${key}DisplayLabel`],
    src[`${key}DisplayText`],
    src[`${key}ValueLabel`],
    src[`${key}Text`],
    src[`${key}Label`],
  );

  if (directLabel) return String(directLabel);

  const labelMaps: unknown[] = [
    (src.valueLabels as Record<string, unknown> | undefined)?.[key],
    (src.optionLabels as Record<string, unknown> | undefined)?.[key],
    src[`${key}Labels`],
    src[`${key}Options`],
  ];

  for (const labelMap of labelMaps) {
    if (Array.isArray(labelMap)) {
      const matchedOption = labelMap.find((option: unknown) => {
        const optionValue = getOptionValue(option);
        return optionValue.toLowerCase() === valueKey.toLowerCase();
      });
      const optionLabel = matchedOption
        ? getOptionLabel(matchedOption)
        : "";
      if (optionLabel) return optionLabel;
      continue;
    }

    const mappedLabel = getLookupValue(labelMap, valueKey);
    if (mappedLabel) return String(mappedLabel);
  }

  return undefined;
};

const getPolicyOutcomeSource = (stepData: unknown) => {
  if (!stepData) return {};
  if (Array.isArray(stepData)) return stepData;
  const data = stepData as Record<string, unknown>;
  if (Array.isArray(data.outcomes) || isObjectValue(data.outcomes)) {
    return data.outcomes;
  }
  if (Array.isArray(data.scenarios) || isObjectValue(data.scenarios)) {
    return data.scenarios;
  }
  return data;
};

const getPolicyValueKey = (value: unknown, fallback: string) => {
  if (!isObjectValue(value)) return fallback;
  return String(
    value.key ??
      value.scenario ??
      value.outcome ??
      value.outcomeKey ??
      value.name ??
      fallback,
  );
};

const getPolicyValueLabel = (value: unknown, fallback: string) => {
  if (!isObjectValue(value)) return toTitleCase(fallback);
  return String(
    value.label ??
      value.scenarioLabel ??
      value.title ??
      value.name ??
      toTitleCase(fallback),
  );
};

const getPolicyValueByKey = (source: unknown, key: string) => {
  if (Array.isArray(source)) {
    return source.find((item) => {
      const itemKey = getPolicyValueKey(item, "");
      return itemKey.toLowerCase() === key.toLowerCase();
    });
  }

  if (!isObjectValue(source)) return undefined;
  if (source[key] !== undefined) return source[key];

  const matchedKey = Object.keys(source).find(
    (itemKey) => itemKey.toLowerCase() === key.toLowerCase(),
  );
  return matchedKey ? source[matchedKey] : undefined;
};

const getPolicyCardsFromSource = (source: unknown) => {
  if (Array.isArray(source)) {
    return source.map((item, index) => {
      const key = getPolicyValueKey(item, `policy-${index + 1}`);
      return {
        key,
        label: getPolicyValueLabel(item, key),
        group: "",
        meta: {},
        value: isObjectValue(item) ? item : { value: item },
      };
    });
  }

  if (!isObjectValue(source)) return [];

  return Object.entries(source).map(([key, value]) => ({
    key,
    label: getPolicyValueLabel(value, key),
    group: "",
    meta: {},
    value: isObjectValue(value) ? value : { value },
  }));
};

const getPayoutPolicyCards = (stepData: unknown, step?: TemplateStepShape) => {
  const source = getPolicyOutcomeSource(stepData);
  const registryRows = step?.outcomeRegistry
    ? getOutcomeRegistryRows(step.outcomeRegistry)
    : [];

  if (registryRows.length === 0) {
    return getPolicyCardsFromSource(source);
  }

  const cards = registryRows
    .filter((row) => row?.key)
    .map((row) => {
      const value = getPolicyValueByKey(source, row.key) || {};
      return {
        key: row.key,
        label: row.label || toTitleCase(row.key),
        group: row.group || "",
        meta: row,
        value: isObjectValue(value) ? value : { value },
      };
    });

  const registryKeys = new Set(cards.map((card) => card.key.toLowerCase()));
  const extraCards = getPolicyCardsFromSource(source).filter(
    (card) => !registryKeys.has(card.key.toLowerCase()),
  );

  return [...cards, ...extraCards];
};

const getComponentValueKey = (component: unknown) => {
  if (!isObjectValue(component)) return String(component ?? "");
  return String(
    component.componentType ??
      component.type ??
      component.key ??
      component.value ??
      component.name ??
      component.label ??
      "",
  );
};

const buildTemplateDisplayMeta = (templateJson?: unknown): TemplateDisplayMeta => {
  const steps = Array.isArray((templateJson as Record<string, unknown> | undefined)?.steps)
    ? ((templateJson as Record<string, unknown>).steps as unknown[])
    : [];
  if (steps.length === 0) return emptyTemplateDisplayMeta;

  const meta: TemplateDisplayMeta = {
    stepLabels: {},
    fieldLabels: {},
    fieldTypes: {},
    optionLabels: {},
  };
  const optionSourceRefs: Array<{ fieldPath: string; sourcePath: string }> = [];

  const normalizeSourcePath = (sourcePath: string, stepId: string): string => {
    const trimmed = sourcePath.trim();
    return trimmed.includes(".") ? trimmed : `${stepId}.${trimmed}`;
  };

  const registerField = (
    field: TemplateFieldShape,
    fieldPath: string,
    stepId: string,
  ) => {
    const label = field?.label ?? field?.title ?? field?.name;
    if (label) meta.fieldLabels[fieldPath] = String(label);
    if (field?.type) meta.fieldTypes[fieldPath] = String(field.type);

    if (Array.isArray(field?.options)) {
      field.options.forEach((option: TemplateOptionShape) => {
        addOptionLabel(
          meta.optionLabels,
          fieldPath,
          getOptionValue(option),
          getOptionLabel(option),
        );
      });
    }

    if (field?.optionsFrom) {
      optionSourceRefs.push({
        fieldPath,
        sourcePath: normalizeSourcePath(String(field.optionsFrom), stepId),
      });
    }

    if (Array.isArray(field?.fields)) {
      field.fields.forEach((nestedField: TemplateFieldShape) => {
        const nestedKey = String(nestedField?.key || "");
        if (!nestedKey) return;
        registerField(nestedField, `${fieldPath}.${nestedKey}`, stepId);
      });
    }
  };

  (steps as TemplateStepShape[]).forEach((step) => {
    const stepId = getTemplateStepId(step);
    const stepLabel = step?.title ?? step?.label ?? step?.name;
    if (stepLabel) meta.stepLabels[stepId] = String(stepLabel);

    if (Array.isArray(step?.fields)) {
      step.fields.forEach((field: TemplateFieldShape) => {
        const fieldKey = String(field?.key || "");
        if (!fieldKey) return;
        registerField(field, `${stepId}.${fieldKey}`, stepId);
      });
    }
  });

  optionSourceRefs.forEach(({ fieldPath, sourcePath }) => {
    const sourceLabels = meta.optionLabels[sourcePath];
    if (!sourceLabels) return;
    meta.optionLabels[fieldPath] = {
      ...sourceLabels,
      ...(meta.optionLabels[fieldPath] ?? {}),
    };
  });

  return meta;
};

type ApiErrorLike = {
  response?: { data?: { message?: unknown } };
  message?: unknown;
};

const getApiErrorMessage = (error: unknown, fallback: string): string => {
  const err = error as ApiErrorLike | null | undefined;
  const responseMessage = err?.response?.data?.message;
  if (responseMessage) return String(responseMessage);
  if (err?.message) return String(err.message);
  return fallback;
};

type UseDealViewRenderersArgs = {
  deal: DealResponseDto | null;
  formData: Record<string, unknown>;
  t: TranslationFn;
  refreshDeal?: () => Promise<DealResponseDto | null>;
  onOpenDealResponseActions?: () => void;
};

export const useDealFieldRenderers = ({
  deal,
  formData,
  t,
  refreshDeal,
  onOpenDealResponseActions,
}: UseDealViewRenderersArgs) => {
   const { i18n } = useTranslation();
  const [downloadingFileKey, setDownloadingFileKey] = useState<string | null>(
    null,
  );

  const templateSteps = (() => {
    const steps = deal?.templateJson?.steps;
    return Array.isArray(steps) ? steps : [];
  })();

  const currentUser = ((): Record<string, unknown> => {
    try {
      return JSON.parse(localStorage.getItem("loginResponse") || "{}");
    } catch {
      return {};
    }
  })();

  const currentUserId = String(currentUser?.id || "").toLowerCase();
  const rawCurrentUsername = String(currentUser?.username || "");
  const currentUsername = rawCurrentUsername.toLowerCase();

  const acceptedBy = asRecord(deal?.acceptedBy);
  const acceptedByProfileDto = asRecord(acceptedBy.profileDto);
  const acceptedByProfile = asRecord(acceptedBy.profile);
  const acceptedById = String(
    acceptedBy.id || acceptedByProfileDto.id || acceptedByProfile.id || "",
  ).toLowerCase();
  const acceptedByUsername = String(
    acceptedBy.username ||
      acceptedByProfileDto.username ||
      acceptedByProfile.username ||
      "",
  ).toLowerCase();

  const isAcceptedUser =
    (currentUserId && acceptedById && currentUserId === acceptedById) ||
    (currentUsername && acceptedByUsername &&
      currentUsername === acceptedByUsername);

  const initiatedBy = asRecord(deal?.initiatedBy);
  const initiatedByProfileDto = asRecord(initiatedBy.profileDto);
  const initiatedByProfile = asRecord(initiatedBy.profile);
  const initiatedById = String(
    initiatedBy.id || initiatedByProfileDto.id || initiatedByProfile.id || "",
  ).toLowerCase();
  const initiatedByUsername = String(
    initiatedBy.username ||
      initiatedByProfileDto.username ||
      initiatedByProfile.username ||
      "",
  ).toLowerCase();

  const isInitiatingUser =
    (currentUserId && initiatedById && currentUserId === initiatedById) ||
    (currentUsername && initiatedByUsername &&
      currentUsername === initiatedByUsername);

 
  const DOCUMENT_UPLOAD_ALLOWED_STATUSES = new Set(["PROPOSED"]);
  const DOCUMENT_REVIEW_ALLOWED_STATUSES = new Set(["PROPOSED"]);

  const isDocumentUploadAllowed =
    isAcceptedUser &&
    DOCUMENT_UPLOAD_ALLOWED_STATUSES.has(String(deal?.status || ""));

  const isDocumentReviewAllowed =
    isInitiatingUser &&
    DOCUMENT_REVIEW_ALLOWED_STATUSES.has(String(deal?.status || ""));

  void onOpenDealResponseActions;

  const uploadRequiredDocument = async (documentType: string, file: File) => {
    if (!documentType.trim()) {
        throw new Error(t("documentTypeMissing"));
      }

      const isPdf =
        file.type === "application/pdf" || /\.pdf$/i.test(file.name);

      if (!isPdf) {
        throw new Error(t("onlyPdfAllowed"));
      }

      if (!deal?.id) {
        throw new Error(t("dealIdMissing"));
      }

      if (!isDocumentUploadAllowed) {
        throw new Error(t("documentUploadNotAllowed"));
      }

      try {
        const uploadedFile = await uploadSingleFile(file);

        await uploadRequiredDocuments({
          dealId: String(deal?.id ?? ""),
          documents: [
            {
              documentType,
              fileUrl: uploadedFile.fileUrl,
              fileName: uploadedFile.fileName,
              fileType: uploadedFile.fileType,
              fileSize: uploadedFile.fileSize,
            },
          ],
        });

        toast.success(t("documentUploadedSuccessfully"));

        if (typeof refreshDeal === "function") {
          await refreshDeal();
        }
      } catch (error) {
        const message = getApiErrorMessage(error, t("unableToUploadDocument"));

        toast.error(message);
        throw error instanceof Error ? error : new Error(String(message));
      }
  };

  const reviewRequiredDocument = async (
    documentType: string,
    status: "APPROVED" | "REJECTED",
    reviewComment?: string,
  ) => {
    if (!documentType.trim()) {
        throw new Error(t("documentTypeMissing"));
      }

      if (!deal?.id) {
        throw new Error(t("dealIdMissing"));
      }

      if (!rawCurrentUsername) {
        throw new Error(t("usernameMissing"));
      }

      if (!isDocumentReviewAllowed) {
        throw new Error(t("documentReviewNotAllowed"));
      }

      try {
        await reviewDealDocument(
          rawCurrentUsername,
          String(deal?.id ?? ""),
          {
            documentType,
            status,
            reviewComment: reviewComment?.trim() || undefined,
          },
        );

        toast.success(
          status === "APPROVED"
            ? t("documentApprovedSuccessfully")
            : t("documentRejectedSuccessfully"),
        );

        if (typeof refreshDeal === "function") {
          await refreshDeal();
        }
      } catch (error) {
        const message = getApiErrorMessage(error, t("unableToReviewDocument"));

        toast.error(message);
        throw error instanceof Error ? error : new Error(String(message));
      }
  };

const templateDisplayMeta = buildTemplateDisplayMeta(deal?.templateJson);

const reverseTranslationMap = useMemo(() => {
  const bundle =
    i18n.getResourceBundle("en", "viewDeal") || {};

  return flattenTranslationValues(bundle);
}, [i18n]);

const tr = useCallback(
  (value?: string) => {
    if (!value) return "";

    const translated = t(value);

    if (translated !== value) {
      return translated;
    }

    const translationKey = reverseTranslationMap[value];

    if (translationKey) {
      return t(translationKey);
    }

    return value;
  },
  [reverseTranslationMap, t],
);

const formatFieldLabel = (key: string, fieldPath = key) =>
  tr(
    templateDisplayMeta.fieldLabels[fieldPath] ||
      templateDisplayMeta.stepLabels[fieldPath] ||
      toTitleCase(key),
  );

const getOptionLabelForValue = (
  fieldPath: string | undefined,
  value: string,
): string | undefined => {
  if (!fieldPath) return undefined;

  const labels = templateDisplayMeta.optionLabels[fieldPath];
  if (!labels) return undefined;

  const label =
    labels[value] ||
    labels[value.toLowerCase()] ||
    labels[value.toUpperCase()];

  return label ? tr(label) : undefined;
};
  const isFileUploadField = (fieldPath?: string): boolean => {
    if (!fieldPath) return false;

    const fieldType = templateDisplayMeta.fieldTypes[fieldPath]?.toLowerCase();
    if (fieldType && FILE_UPLOAD_TYPES.has(fieldType)) return true;

    const fieldKey = fieldPath.split(".").pop() || "";
    return /file|attachment/i.test(fieldKey);
  };

  const shouldRenderFileValue = (
    value: unknown,
    fieldPath?: string,
  ): boolean => {
    const files = normalizeFileItems(value);
    if (files.length === 0) return false;
    if (isFileUploadField(fieldPath)) return true;
    return files.some(hasFileExtension);
  };

  const handleDownloadFile = async (file: FileDisplayItem, fileKey: string) => {
    if (!file.url || downloadingFileKey === fileKey) return;

    setDownloadingFileKey(fileKey);
    try {
      await downloadFileFromUrl(file.url, file.name);
    } catch {
      toast.error(t("downloadFailed"));
    } finally {
      setDownloadingFileKey(null);
    }
  };

  const renderFileValue = (value: unknown): React.ReactNode => {
    const files = normalizeFileItems(value);
    if (files.length === 0) return "-";

      return (
        <div className="max-w-full space-y-2">
          {files.map((file, index) => {
            const fileName = file.name
              ? hasFileExtension(file)
                ? file.name
                : toTitleCase(file.name)
              : t("viewFile");
            const fileType = file.extension || t("document");
            const fileKey = `${file.url || fileName}-${index}`;
            const isDownloading = downloadingFileKey === fileKey;

            return (
              <div
                key={fileKey}
                className="flex min-w-0 max-w-full items-center gap-2 overflow-hidden rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-2 shadow-sm"
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-blue-50 text-blue-600">
                  <DocumentTextIcon className="h-5 w-5" />
                </div>

                <div className="min-w-0 flex-1">
                  {file.url ? (
                    <button
                      type="button"
                      disabled={isDownloading}
                      onClick={() => void handleDownloadFile(file, fileKey)}
                      className="block w-full truncate text-left text-sm font-semibold text-gray-900 hover:text-blue-700 disabled:cursor-wait disabled:text-gray-500"
                      title={fileName}
                    >
                      {fileName}
                    </button>
                  ) : (
                    <p
                      className="truncate text-sm font-semibold text-gray-900"
                      title={fileName}
                    >
                      {fileName}
                    </p>
                  )}
                  <p className="text-[11px] font-medium text-gray-500">
                    {fileType}
                  </p>
                </div>

                {file.url && (
                  <button
                    type="button"
                    disabled={isDownloading}
                    onClick={() => void handleDownloadFile(file, fileKey)}
                    className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:cursor-wait disabled:opacity-60"
                    aria-label={t("downloadFile", { fileName })}
                    title={t("download")}
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      );
  };

  const formatFieldValue = (
    value: unknown,
    fieldPath?: string,
  ): React.ReactNode => {
    if (value === null || value === undefined) return "-";
      if (shouldRenderFileValue(value, fieldPath)) return renderFileValue(value);
      if (typeof value === "boolean") return value ? t("yes") : t("no");
      if (typeof value === "number") return String(value);

      if (typeof value === "string") {
        const trimmedValue = value.trim();
        const optionLabel = getOptionLabelForValue(fieldPath, trimmedValue);
        if (optionLabel) return optionLabel;
        if (!trimmedValue) return "-";
        const dateValue = formatDateValue(trimmedValue, fieldPath);
        if (dateValue) return dateValue;
        if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedValue)) return trimmedValue;
        if (isHttpUrl(trimmedValue)) {
          const fileName = getFileNameFromUrl(trimmedValue);
          return (
            <a
              href={trimmedValue}
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-blue-600 hover:text-blue-700"
              title={trimmedValue}
            >
              {fileName || t("viewFile")}
            </a>
          );
        }
       if (trimmedValue.startsWith("{") && trimmedValue.endsWith("}")) {
  return tr(toTitleCase(trimmedValue.slice(1, -1).trim()));
}
        return tr(toTitleCase(trimmedValue));
      }

      if (Array.isArray(value)) {
        if (value.length === 0) return "-";
        return value
          .map((item) =>
            typeof item === "string"
              ? isHttpUrl(item.trim())
                ? getFileNameFromUrl(item.trim()) || item
                : formatFieldValue(item, fieldPath)
              : typeof item === "number"
                ? String(item)
                : typeof item === "boolean"
                  ? item
                    ? t("yes")
                    : t("no")
                  : "-",
          )
          .filter(Boolean)
          .join(", ");
      }

      if (typeof value === "object") {
        const entries = Object.entries(value);
        const previewParts = entries
          .filter(([, entryValue]) => entryValue !== null && entryValue !== undefined)
          .slice(0, 4)
          .map(([entryKey, entryValue]) => {
            const entryPath = fieldPath ? `${fieldPath}.${entryKey}` : entryKey;
            if (Array.isArray(entryValue)) {
              const arr = entryValue
                .map((x) =>
                  typeof x === "string"
                    ? formatFieldValue(x, entryPath)
                    : typeof x === "number"
                      ? String(x)
                      : "-",
                )
                .join(", ");
              return `${formatFieldLabel(entryKey, entryPath)}: ${arr}`;
            }

            if (typeof entryValue === "object") {
              return `${formatFieldLabel(entryKey, entryPath)}: {...}`;
            }

            return `${formatFieldLabel(entryKey, entryPath)}: ${formatFieldValue(entryValue, entryPath)}`;
          });

        return previewParts.length > 0 ? previewParts.join(", ") : "-";
      }

      return String(value);
  };

  const policySummaryByScenario = (() => {
    const summaryMap: Record<string, SourceShape> = {};
    const scenarios = deal?.dealPolicySummaryDto?.scenarios;

    if (!Array.isArray(scenarios)) return summaryMap;

    scenarios.forEach((scenario) => {
      [scenario?.scenario, scenario?.scenarioLabel].forEach((key) => {
        if (!key) return;
        summaryMap[String(key).toLowerCase()] = asRecord(scenario);
      });
    });

    return summaryMap;
  })();

  const getComponentLabel = (component: unknown): string => {
    const componentKey = getComponentValueKey(component);

    if (componentKey) {
      const templateLabel = getOptionLabelForValue(
        "components.componentType",
        componentKey,
      );
      if (templateLabel) return templateLabel;

      const savedComponent = Array.isArray(formData?.components)
        ? formData.components.find((item: unknown) => {
            const savedKey = getComponentValueKey(item);
            return savedKey.toLowerCase() === componentKey.toLowerCase();
          })
        : null;

      const savedComponentShape = savedComponent as SourceShape | undefined;
      const savedLabel =
        savedComponentShape?.label ||
        savedComponentShape?.componentLabel ||
        savedComponentShape?.name;

      if (savedLabel) return String(savedLabel);
    }

    if (isObjectValue(component)) {
      const directLabel =
        component.label || component.componentLabel || component.name;
      if (directLabel) return String(directLabel);
    }

    return componentKey ? toTitleCase(componentKey) : "-";
  };

  const renderPayoutPolicies = (
    stepId: string,
    stepData: unknown,
    step?: TemplateStepShape,
  ): React.ReactNode => {
    const cards = getPayoutPolicyCards(stepData, step);

    if (cards.length === 0) {
      return <div className="text-sm text-gray-500">-</div>;
    }

      const formDataCore = (formData?.core ?? {}) as Record<string, unknown>;
      const baseAmount = Number(
        deal?.dealPolicySummaryDto?.baseAmount ??
          formDataCore.baseAmount ??
          formData?.baseAmount ??
          0,
      );

      const rawCurrency = String(
        deal?.dealPolicySummaryDto?.currency ||
          formDataCore.currency ||
          "EUR",
      );

      const currencySymbol =
        rawCurrency === "EUR" ? "€" : rawCurrency === "USD" ? "$" : rawCurrency;

      const knownPolicyFields = new Set([
        "key",
        "label",
        "title",
        "name",
        "scenario",
        "scenarioLabel",
        "outcome",
        "outcomeKey",
        "base",
        "baseIncluded",
        "includesBase",
        "baseLocked",
        "baseSelectable",
        "componentsSelectable",
        "optionsFrom",
        "selectionMode",
        "appliedPayoutType",
        "mode",
        "policyType",
        "selectedComponents",
        "components",
        "componentTypes",
        "includedComponents",
        "excludedComponents",
      ]);

      const groupNames = cards
        .map((card) => card.group || "")
        .filter((groupName, index, list) => list.indexOf(groupName) === index);

      return (
        <div className="space-y-4">
          {groupNames.map((groupName, groupIndex) => {
            const groupCards = cards.filter(
              (card) => (card.group || "") === groupName,
            );

            return (
              <div key={groupName || `policy-group-${groupIndex}`} className="space-y-4">
                {groupName && (
                  <div className="flex items-center gap-3">
                    <p className="text-xs font-semibold text-gray-500">
                      {tr(groupName)}
                    </p>
                    <div className="h-px flex-1 bg-gray-200" />
                  </div>
                )}

                {groupCards.map((card) => {
                  const value = (card.value || {}) as SourceShape & {
                    base?: { included?: unknown; locked?: unknown };
                  };
                  const meta = (card.meta || {}) as SourceShape;
                  const summary =
                    policySummaryByScenario[card.key.toLowerCase()] ||
                    policySummaryByScenario[
                      String(value.scenario || value.scenarioLabel || "").toLowerCase()
                    ];

                  const baseIncluded = getFirstValue(
                    value.baseIncluded,
                    value.includesBase,
                    value.base?.included,
                    meta.baseIncluded,
                    summary?.baseIncluded,
                  );
                  const baseLocked = getFirstValue(
                    value.baseLocked,
                    value.base?.locked,
                    meta.baseLocked,
                  );
                  const selectedComponentsEntry = getFirstFieldEntry(
                    {
                      key: "selectedComponents",
                      value: value.selectedComponents,
                      source: value,
                    },
                    { key: "components", value: value.components, source: value },
                    {
                      key: "componentTypes",
                      value: value.componentTypes,
                      source: value,
                    },
                    {
                      key: "includedComponents",
                      value: value.includedComponents,
                      source: value,
                    },
                    {
                      key: "includedComponents",
                      value: summary?.includedComponents,
                      source: summary,
                    },
                  );
                  const selectedComponentsValue = selectedComponentsEntry?.value;
                  const selectedComponents = Array.isArray(selectedComponentsValue)
                    ? selectedComponentsValue
                    : selectedComponentsValue
                      ? [selectedComponentsValue]
                      : [];
                  const rawBaseLockedDisplay =
                    typeof baseLocked === "boolean"
                      ? getFirstValue(
                          getValueLabelFromSource(value, "baseLocked", baseLocked),
                          getValueLabelFromSource(meta, "baseLocked", baseLocked),
                          getValueLabelFromSource(
                            summary,
                            "baseLocked",
                            baseLocked,
                          ),
                        )
                      : undefined;
                  const baseLockedDisplay =
                    rawBaseLockedDisplay === undefined ||
                    rawBaseLockedDisplay === null
                      ? undefined
                      : String(rawBaseLockedDisplay);

                  const extraFields = Object.entries(value).filter(([key, item]) => {
                    if (knownPolicyFields.has(key)) return false;
                    if (item === null || item === undefined || item === "") return false;
                    if (Array.isArray(item) && item.length === 0) return false;
                    if (isObjectValue(item) && Object.keys(item).length === 0) return false;
                    return true;
                  });

                  // Check if base is included
                  const isBaseIncluded = baseIncluded !== false;

                  // Resolve selected components
                  const resolvedSelectedComponents = selectedComponents
                    .map((comp: unknown, compIndex: number) => {
                      const compKey = getComponentValueKey(comp);
                      if (!compKey) return null;

                      let compItem: SourceShape | null = Array.isArray(
                        formData?.components,
                      )
                        ? ((formData.components.find((c: unknown) => {
                            const k = getComponentValueKey(c);
                            return (
                              k.toLowerCase() === compKey.toLowerCase() ||
                              String(
                                (c as SourceShape)?.componentType || "",
                              ).toLowerCase() === compKey.toLowerCase()
                            );
                          }) ?? null) as SourceShape | null)
                        : null;

                      if (!compItem && Array.isArray(deal?.componentPayments)) {
                        for (const p of deal?.componentPayments ?? []) {
                          const payment = asRecord(p);
                          const items = [
                            ...(Array.isArray(payment?.preMatchBreakdown)
                              ? payment.preMatchBreakdown
                              : []),
                            ...(Array.isArray(payment?.postMatchBreakdown)
                              ? payment.postMatchBreakdown
                              : []),
                          ];
                          const found = items.find(
                            (item: unknown) =>
                              String(
                                (item as Record<string, unknown>)?.componentType ||
                                  "",
                              ).toLowerCase() ===
                              compKey.toLowerCase(),
                          );
                          if (found) {
                            compItem = found as SourceShape;
                            break;
                          }
                        }
                      }

                      let amount = 0;
                      let isPenalty =
                        /penalty|deduct/i.test(compKey) ||
                        /penalty|deduct/i.test(String(compItem?.componentType || ""));

                      if (compItem) {
                        const rawVal = Number(
                          compItem.amount ??
                            compItem.resolvedAmount ??
                            compItem.rawValue ??
                            compItem.value ??
                            0,
                        );
                        const isPercent =
                          compItem.calculationMode === "PERCENT_OF_BASE" ||
                          compItem.mode === "PERCENT_OF_BASE";
                        amount = isPercent ? (baseAmount * rawVal) / 100 : rawVal;
                        if (rawVal < 0) isPenalty = true;
                      }

                      const label = getComponentLabel(compItem || comp);
                      const absAmount = Math.abs(amount);
                      const formattedAmount = isPenalty ? `-${absAmount}` : `${absAmount}`;

                      return {
                        key: `${compKey}-${compIndex}`,
                        label,
                        amount: isPenalty ? -absAmount : absAmount,
                        absAmount,
                        isPenalty,
                        formattedAmount,
                      };
                    })
                    .filter(Boolean) as Array<{
                      key: string;
                      label: string;
                      amount: number;
                      absAmount: number;
                      isPenalty: boolean;
                      formattedAmount: string;
                    }>;

                  let finalTotal = 0;
                  if (summary?.payoutAmount !== undefined && summary?.payoutAmount !== null) {
                    finalTotal = Number(summary.payoutAmount);
                  } else {
                    const compTotal = resolvedSelectedComponents.reduce(
                      (acc, curr) => acc + curr.amount,
                      0,
                    );
                    finalTotal = (isBaseIncluded ? baseAmount : 0) + compTotal;
                  }

                  return (
                    <div
                      key={`${stepId}-${card.key}`}
                      className="flex flex-col gap-3 p-5 sm:p-6 rounded-2xl sm:rounded-3xl border border-gray-200/80 bg-white shadow-none"
                    >
                      {/* Card header */}
                      <div className="mb-4">
                        <div className="flex items-center gap-3">
                          <h3 className="text-sm font-bold text-gray-900 font-['Plus_Jakarta_Sans'] leading-5">
                            {tr(card.label)}
                          </h3>
                          {baseLockedDisplay && (
                            <span
                              className={[
                                "rounded-xl border px-1.5 py-0.5 text-xs font-medium",
                                baseLocked
                                  ? "border-blue-200 bg-blue-100 text-blue-700"
                                  : "border-gray-200 bg-white text-gray-500",
                              ].join(" ")}
                            >
                              {tr(String(baseLockedDisplay))}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5 font-['Plus_Jakarta_Sans'] leading-4">
                          {t("basePlusSelectedComponents")}
                        </p>
                      </div>

                      {/* Rows — two-column layout matching Figma w-80 gap-4 */}
                      <div className="space-y-3 w-full">
                        {/* Base amount row */}
                        {isBaseIncluded && (
                          <div className="flex items-start gap-4">
                            <span className="w-56 sm:w-64 shrink-0 text-sm sm:text-base font-normal text-gray-900 font-['Plus_Jakarta_Sans'] leading-6">
                              {t("baseAmount")}
                            </span>
                            <span className="text-left text-sm sm:text-base font-normal text-gray-900 font-['Plus_Jakarta_Sans'] leading-6">
                              {baseAmount} {currencySymbol}
                            </span>
                          </div>
                        )}

                        {/* Selected component rows */}
                        {resolvedSelectedComponents.map((comp) => (
                          <div key={comp.key} className="flex items-start gap-4">
                            <span className="w-56 sm:w-64 shrink-0 text-sm sm:text-base font-normal text-gray-900 font-['Plus_Jakarta_Sans'] leading-6">
                              {comp.label}
                            </span>
                            <span className="text-left text-sm sm:text-base font-normal text-gray-900 font-['Plus_Jakarta_Sans'] leading-6">
                              {comp.formattedAmount} {currencySymbol}
                            </span>
                          </div>
                        ))}

                        {/* Extra fields if any */}
                        {extraFields.map(([key, item]) => (
                          <div key={key} className="flex items-start gap-4">
                            <span className="w-56 sm:w-64 shrink-0 text-sm sm:text-base font-normal text-gray-900 font-['Plus_Jakarta_Sans'] leading-6">
                              {formatFieldLabel(key, `${stepId}.${key}`)}
                            </span>
                            <span className="text-left text-sm sm:text-base font-normal text-gray-900 font-['Plus_Jakarta_Sans'] leading-6">
                              {formatFieldValue(item, `${stepId}.${key}`)}
                            </span>
                          </div>
                        ))}

                        {/* Divider + Total row */}
                        <div className="border-t border-gray-200 pt-3 mt-3">
                          <div className="flex items-start gap-4">
                            <span className="w-56 sm:w-64 shrink-0 text-sm sm:text-base font-bold text-gray-900 font-['Plus_Jakarta_Sans'] leading-6">
                              {t("total")}
                            </span>
                            <span className="text-left text-sm sm:text-base font-bold text-gray-900 font-['Plus_Jakarta_Sans'] leading-6 tabular-nums">
                              {finalTotal} {currencySymbol}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      );
  };

  const renderNestedObjectGrid = (
    obj: unknown,
    basePath?: string,
    columns: 2 | 3 = 2,
  ): React.ReactNode => {
    if (!obj || typeof obj !== "object") return "-";

    const gridClass =
      columns === 3
        ? "grid-cols-1 sm:grid-cols-2 xl:grid-cols-3"
        : "grid-cols-2";

    return (
      <div className={`grid ${gridClass} gap-x-6 gap-y-4`}>
          {Object.entries(obj).map(([k, v]) => {
            const fieldPath = basePath ? `${basePath}.${k}` : k;
            if (v === null || v === undefined || v === "") return null;
            if (Array.isArray(v) && v.length === 0) return null;
            if (
              typeof v === "object" &&
              !Array.isArray(v) &&
              Object.keys(v).length === 0
            ) {
              return null;
            }
            const renderAsFileValue = shouldRenderFileValue(v, fieldPath);
            const fileSpanClass =
              columns === 3 ? "xl:col-span-2" : "col-span-2";

            return (
              <div
                key={k}
                className={`min-w-0 ${renderAsFileValue ? fileSpanClass : ""}`}
              >
                <div className="text-sm font-medium text-gray-500 font-['Plus_Jakarta_Sans'] leading-5">
                  {formatFieldLabel(k, fieldPath)}
                </div>
                <div className="text-base font-medium text-gray-900 break-words font-['Plus_Jakarta_Sans'] leading-6">
                  {formatFieldValue(v, fieldPath)}
                </div>
              </div>
            );
          })}
        </div>
      );
  };

  const renderDynamicObject = (
    obj: unknown,
    skip = 0,
  ): React.ReactNode => {
    if (!obj || typeof obj !== "object") return "-";

      return (
        <div className="grid grid-cols-2 gap-x-6 gap-y-5">
          {Object.entries(obj)
            .slice(skip)
            .map(([fieldKey, fieldValue]) => {
              const fieldPath = fieldKey;

              if (
                fieldValue === null ||
                fieldValue === undefined ||
                fieldValue === ""
              ) {
                return null;
              }

              if (Array.isArray(fieldValue) && fieldValue.length === 0) {
                return null;
              }

              if (
                typeof fieldValue === "object" &&
                !Array.isArray(fieldValue) &&
                Object.keys(fieldValue).length === 0
              ) {
                return null;
              }

              const step = templateSteps.find(
                (item) => getTemplateStepId(item) === fieldKey,
              );

              const hasOutcomeData =
                isObjectValue(fieldValue) &&
                (Array.isArray(fieldValue.outcomes) ||
                  isObjectValue(fieldValue.outcomes) ||
                  Array.isArray(fieldValue.scenarios) ||
                  isObjectValue(fieldValue.scenarios));

              const isPayoutPolicyStep =
                !!step?.outcomeRegistry ||
                /payout\s*policies/i.test(String(step?.title || "")) ||
                (/polic/i.test(fieldKey) && hasOutcomeData);

              const renderAsFileValue = shouldRenderFileValue(
                fieldValue,
                fieldPath,
              );

              if (isPayoutPolicyStep) {
                return (
                  <div key={fieldKey} className="col-span-2">
                    {renderPayoutPolicies(fieldKey, fieldValue, step)}
                  </div>
                );
              }

              if (renderAsFileValue) {
                return (
                  <div key={fieldKey} className="col-span-2 min-w-0">
                    <div className="text-sm leading-5 text-gray-500">
                      {formatFieldLabel(fieldKey, fieldPath)}
                    </div>
                    <div className="mt-1 break-words text-sm font-semibold leading-5 text-gray-900">
                      {formatFieldValue(fieldValue, fieldPath)}
                    </div>
                  </div>
                );
              }

              if (Array.isArray(fieldValue)) {
                const nestedGridColumns = fieldKey === "components" ? 3 : 2;

                const allPrimitive = fieldValue.every(
                  (item) =>
                    item === null ||
                    item === undefined ||
                    ["string", "number", "boolean"].includes(typeof item),
                );

                return (
                  <div key={fieldKey} className="col-span-2">
                    <h3 className="mb-3 text-base font-bold text-gray-900">
                      {formatFieldLabel(fieldKey, fieldPath)}
                    </h3>

                    {allPrimitive ? (
                      <div className="break-words text-sm font-semibold leading-5 text-gray-900">
                        {fieldValue
                          .map((item) => formatFieldValue(item, fieldPath))
                          .join(", ")}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {fieldValue.map((arrayItem, arrayIndex) => (
                          <div
                            key={arrayIndex}
                            className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm"
                          >
                            {typeof arrayItem === "object" &&
                            arrayItem !== null ? (
                              renderNestedObjectGrid(
                                arrayItem,
                                fieldPath,
                                nestedGridColumns,
                              )
                            ) : (
                              <div className="text-sm font-semibold leading-5 text-gray-900">
                                {formatFieldValue(arrayItem, fieldPath)}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              if (typeof fieldValue === "object") {
                return (
                  <div key={fieldKey} className="col-span-2">
                    <h3 className="mb-3 text-base font-bold text-gray-900">
                      {formatFieldLabel(fieldKey, fieldPath)}
                    </h3>
                    <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
                      {renderNestedObjectGrid(fieldValue, fieldPath)}
                    </div>
                  </div>
                );
              }

              return (
                <div key={fieldKey} className="min-w-0">
                  <div className="text-sm font-medium text-gray-500 font-['Plus_Jakarta_Sans'] leading-5">
                    {formatFieldLabel(fieldKey, fieldPath)}
                  </div>
                  <div className="text-base font-medium text-gray-900 break-words font-['Plus_Jakarta_Sans'] leading-6">
                    {formatFieldValue(fieldValue, fieldPath)}
                  </div>
                </div>
              );
            })}
        </div>
      );
  };

  const renderSummary = (obj: unknown): React.ReactNode => {
    if (!obj || typeof obj !== "object" || Object.keys(obj).length === 0) {
        return <div className="text-sm text-gray-500">{t("noSummaryData")}</div>;
      }

      const entries = Object.entries(obj).slice(0, 3);

      return (
        <div className="space-y-6">
          {entries.map(([groupKey, groupValue]) => (
            <div key={groupKey} className="flex flex-col gap-2">
              {/* Section header — border-bottom separator style matching Figma */}
              <div className="border-b border-gray-200 pb-1">
                <span className="text-base font-semibold text-gray-500 font-['Plus_Jakarta_Sans'] leading-6">
                  {formatFieldLabel(groupKey, groupKey)}
                </span>
              </div>

              {typeof groupValue === "object" &&
              groupValue !== null &&
              !Array.isArray(groupValue) ? (
                <div className="grid grid-cols-2 gap-x-4 gap-y-3 pt-1">
                  {Object.entries(groupValue).map(([k, v]) => {
                    const fieldPath = `${groupKey}.${k}`;
                    if (v === null || v === undefined || v === "") return null;
                    if (Array.isArray(v) && v.length === 0) return null;
                    if (
                      typeof v === "object" &&
                      !Array.isArray(v) &&
                      Object.keys(v).length === 0
                    ) {
                      return null;
                    }
                    const renderAsFileValue = shouldRenderFileValue(v, fieldPath);

                    return (
                      <div
                        key={k}
                        className={`min-w-0 flex flex-col gap-0.5 ${
                          renderAsFileValue ? "col-span-2" : ""
                        }`}
                      >
                        {/* Label — Figma: text-sm font-medium muted */}
                        <div className="text-sm font-medium text-gray-500 font-['Plus_Jakarta_Sans'] leading-5">
                          {formatFieldLabel(k, fieldPath)}
                        </div>
                        {/* Value — Figma: text-base font-medium foreground */}
                        <div className="text-base font-medium text-gray-900 break-words font-['Plus_Jakarta_Sans'] leading-6">
                          {formatFieldValue(v, fieldPath)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-base font-medium text-gray-900 font-['Plus_Jakarta_Sans'] leading-6 break-words">
                  {formatFieldValue(groupValue, groupKey)}
                </div>
              )}
            </div>
          ))}
        </div>
      );
  };

  return {
    renderDynamicObject,
    renderSummary,
    tr,
    uploadRequiredDocument,
    reviewRequiredDocument,
  };
};
